'use client';
import { MenuItems, useCurrentSportMenuItems, useCurrentSubMenuItems } from '@/hooks/query';
import { SportsMenuItemEdit } from './components/sports-menu-item-edit';
import { MenuIcon } from '@/components/icons/menu-icon';
import { useEffect, ContextType, useRef } from 'react';
import { useParams } from 'next/navigation';
import { VisibilityContext } from 'react-horizontal-scrolling-menu';
import { ScrollingWrapper } from './scroll-menu';
import { useSiteStore } from '@/store/site';
import { isDummySportCategory, isNumberGames, isVirtualSports } from '@/services/settings.service';
import { useAccountInfo } from '@/hooks/query/account';
import { useTranslations } from 'next-intl';
import { useSportMenuActived } from '@/hooks/use-sport-menu-actived';
import { matchSome } from '@nf/utils-common/fpw/monadic';
import { getDummySetting } from '@/utils/saba-sports-util';

type scrollVisibilityApiType = ContextType<typeof VisibilityContext>;

const filterMenuItems = (menuItems: MenuItems[], sport: number, isShowVirtuallySoccerMenu: (index: number) => boolean|undefined) => {
	return menuItems.filter((item, index) => {
		const gameId = Number(item.gameId);
		const isVirtualSportMenu = isVirtualSports(gameId);
		const isNumberGameMenu = isNumberGames(gameId);
		const isOutright = gameId === 999;
		const isShowVirtuallySoccer = isShowVirtuallySoccerMenu(index);
		
		return matchSome(sport)
			.on(isVirtualSports, () => isVirtualSportMenu || item.isVirtualTournament)
			.on(isNumberGames, () => isNumberGameMenu)
			.otherwise(() => !isVirtualSportMenu && !isNumberGameMenu && !isOutright || isShowVirtuallySoccer);
	});
};
export const SportsMenu = () => {
	const { sport: originSport } = useParams();
	const apiRef = useRef({} as scrollVisibilityApiType);
	const isParlayMode = useSiteStore(state => state.isParlayMode);
	const currentSportMenuItems = useCurrentSportMenuItems(isParlayMode);
	const subMenuItemsOfCurrentSport = useCurrentSubMenuItems(isParlayMode);
	const dummySetting = getDummySetting(Number(originSport));
	const sport = dummySetting?.categoryId.toString() ?? originSport;
	const isInVirtualSports = isVirtualSports(Number(sport));
	
	const { user } = useAccountInfo();
	const generalResource = useTranslations('General');
	const { isDummySportCategoryActived } = useSportMenuActived();

	// 因left menu的virtual sport 生成為子選單的形式, 因此在這邊用isVirtualSports來判斷要拿一般menu 或是 subMenu 的資料
	const baseMenuItems = !isInVirtualSports
		? currentSportMenuItems 
		: subMenuItemsOfCurrentSport;

	let menuItems = baseMenuItems;

	// VirtualSport有賽事時才加入VSTournament
	if (user?.VSTournament
		&& user?.ActStatus === 2 
		&& isInVirtualSports 
		&& baseMenuItems.length > 0
	) {
		const vsTournamentItem = {
			'hasLive': false,
			'name': generalResource('lbl_VSTournament'),
			'icon': 'virtual-tournament',
			'href': '/third-party/vs-tournament',
			'gameId': '',
			'matchCount': 0,
			'isVirtualTournament': true,
		};

		menuItems = [vsTournamentItem, ...baseMenuItems];
	}

	const firstVirtualSoccerIndex = menuItems.findIndex(item => parseInt(item.gameId) === 147);
	const isShowVirtuallySoccerMenu = (index: number) => (index === firstVirtualSoccerIndex && user?.VirtuallySoccerSportsMenu);
	const filteredMenuItems = filterMenuItems(menuItems, Number(sport), isShowVirtuallySoccerMenu);

	useEffect(() => {
		const current = filteredMenuItems.find(item => item.gameId === sport) || filteredMenuItems[0];
		const timer = setTimeout(() => {
			apiRef.current?.scrollToItem?.(
				apiRef.current?.getItemById(String(current?.gameId)), 'smooth', 'center', 'nearest'
			);
		}, 100);
		
		return () => {
			clearTimeout(timer);
		};
	}, [sport, filteredMenuItems]);

	return (
		<>
			<ScrollingWrapper
				enableLeftArrow={filteredMenuItems.length > 5}
				apiRef={apiRef}
			>
				{filteredMenuItems.map((item, index) => (
					<MenuIcon
						key={`${item.gameId}-${index}`}
						index={index}
						menuItem={item}
						isActive={isDummySportCategory(Number(item.gameId)) 
							? isDummySportCategoryActived(Number(item.gameId)) 
							: item.gameId === sport
						}
						name={item.name}
						isNew={['177', '198'].includes(item.gameId)}
					/>
				))}
			</ScrollingWrapper>
			<nav className="sports-menu__more">
				<SportsMenuItemEdit />
			</nav>
		</>
	);
};
